import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO';
import Navigation from './Navigation';
import SocialShare from './SocialShare';

function HomePage() {
  const [isLoading, setIsLoading] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    vehicle: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  // Human verification state
  const [captchaNumbers, setCaptchaNumbers] = useState({ num1: 0, num2: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaCorrect, setCaptchaCorrect] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Show loader for 1 second
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Generate captcha on component mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaNumbers({ num1, num2 });
    setCaptchaAnswer('');
    setCaptchaCorrect(false);
  };

  const handleCaptchaChange = (e) => {
    const answer = e.target.value;
    setCaptchaAnswer(answer);
    const correctAnswer = captchaNumbers.num1 + captchaNumbers.num2;
    setCaptchaCorrect(parseInt(answer) === correctAnswer);
  };

  const handleContactFormChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Create FormData object for proper form submission
      const formDataToSend = new FormData();
      formDataToSend.append('name', contactForm.name);
      formDataToSend.append('phone', contactForm.phone);
      formDataToSend.append('email', contactForm.email);
      formDataToSend.append('vehicle', contactForm.vehicle);
      formDataToSend.append('message', contactForm.message);
      formDataToSend.append('_subject', 'New Contact Form Submission - Carkey Solutions');
      formDataToSend.append('_template', 'table');
      formDataToSend.append('_captcha', 'false');
      formDataToSend.append('_next', window.location.href);

      const response = await fetch('https://formsubmit.co/sssreeej@yahoo.com', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setContactForm({
          name: '',
          phone: '',
          email: '',
          vehicle: '',
          message: ''
        });
        generateCaptcha();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };



  return (
    <div className="App">
      <SEO />
      {/* Page Loader */}
      {isLoading && (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-gray-900 to-black z-[9999] flex items-center justify-center">
          <div className="text-center">
            {/* Logo Container */}
            <div className="relative mb-8">
              <div className="flex items-center justify-center mb-6">
                                  <img 
                    src="/logo/LG.png" 
                    alt="CARKEY SOLUTIONS" 
                    className="h-20 w-auto animate-logo-fade-in" 
                  />
              </div>
              <div className="text-4xl font-bold text-white animate-text-fade-in">
                <span className="text-carkey-red">Car</span>key Solutions
              </div>
            </div>
            
            {/* Loading Animation */}
            <div className="flex items-center justify-center space-x-2 mb-8">
              <div className="w-3 h-3 bg-carkey-red rounded-full animate-bounce"></div>
              <div className="w-3 h-3 bg-carkey-red rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
              <div className="w-3 h-3 bg-carkey-red rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            </div>
            
            {/* Loading Text */}
            <div className="text-gray-400 text-lg font-medium animate-pulse">
              Unlocking Your Car Key Solutions...
            </div>
            
            {/* Progress Bar */}
            <div className="w-64 h-1 bg-gray-700 rounded-full mt-6 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-carkey-red to-red-500 rounded-full animate-progress-bar"></div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <Navigation />

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black overflow-hidden">
        {/* Premium Background Effects */}
        <div className="absolute inset-0">
          {/* Subtle gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-br from-carkey-red/5 via-transparent to-blue-900/10"></div>
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent"></div>
          
          {/* Sophisticated geometric patterns */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-carkey-red/10 to-transparent rounded-full blur-3xl animate-float-slow"></div>
          <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-gradient-to-l from-blue-500/10 to-transparent rounded-full blur-3xl animate-float-slow-reverse"></div>
          
          {/* Premium grid pattern */}
          <div className="absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23ffffff' stroke-width='1' stroke-opacity='0.5'%3E%3Cpath d='M0 50h100M50 0v100'/%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        {/* Navigation spacer */}
        <div className="pt-20"></div>
        
        <div className="container-max px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center min-h-[calc(100vh-5rem)] py-4 sm:py-6 lg:py-0">
            
            {/* Left Content */}
            <div className="lg:w-1/2 lg:pr-12 mb-6 lg:mb-0">
              {/* Premium Badge */}
              <div className="inline-flex items-center bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-md border border-white/20 text-white rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-4 sm:mb-6 shadow-xl hover:bg-white/20 transition-all duration-500">
                <div className="w-2 h-2 bg-carkey-red rounded-full mr-3 animate-pulse"></div>
                <span className="text-sm font-medium tracking-wide">PERTH'S PREMIER MOBILE KEY SERVICE</span>
              </div>
              
              {/* Hero Title */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                <span className="block opacity-0 animate-fade-in-up">Premium</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-carkey-red via-red-400 to-orange-400 opacity-0 animate-fade-in-up-delay-1">
                  Car Key
                </span>
                <span className="block opacity-0 animate-fade-in-up-delay-2">Solutions Perth</span>
                <span className="block text-2xl md:text-3xl lg:text-4xl text-gray-300 font-light mt-4 opacity-0 animate-fade-in-up-delay-3">
                  Professional • Precision • Performance
                </span>
              </h1>
              
              {/* Description */}
              <p className="text-lg sm:text-xl text-gray-300 mb-6 sm:mb-8 leading-relaxed max-w-lg opacity-0 animate-fade-in-up-delay-4">
                <span className="text-white font-semibold">Car key in Perth?</span> Lost keys? Broken key fob? Perth's #1 mobile car key replacement service. Our certified technicians deliver 
                <span className="text-white font-semibold"> precision-engineered solutions</span> directly to your location across Perth, Fremantle, Joondalup, Rockingham & WA.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 opacity-0 animate-fade-in-up-delay-7">
                <button 
                  onClick={() => navigate('/quote')} 
                  className="group relative bg-gradient-to-r from-carkey-red to-red-600 text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-500 hover:shadow-2xl hover:shadow-carkey-red/25 hover:-translate-y-1 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-700 translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
                  <span className="relative flex items-center justify-center">
                    <i className="fas fa-paper-plane mr-3"></i>
                    Request a Quote
                  </span>
                </button>
                
                <a 
                  href="tel:+61430168669" 
                  className="group bg-white/10 backdrop-blur-md border border-white/30 text-white px-8 py-4 rounded-2xl font-semibold text-lg text-center transition-all duration-500 hover:bg-white hover:text-gray-900 hover:-translate-y-1"
                >
                  <i className="fas fa-phone mr-3"></i>
                  Emergency Call
                </a>
              </div>
            </div>
            
            {/* Right Column - Service Options */}
            <div className="lg:w-1/2 relative opacity-0 animate-fade-in-right">
              <div className="relative max-w-md mx-auto -mt-8 sm:-mt-12 lg:mt-0">
                {/* Service Options Container */}
                <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-4 sm:p-6 shadow-2xl">
                  
                  {/* Header */}
                  <div className="text-center mb-4 sm:mb-6">
                    <h2 className="text-2xl font-bold text-carkey-red mb-1">Need a Key Solution?</h2>
                    <p className="text-gray-300 text-sm">Choose your situation below</p>
                  </div>
                  
                  {/* Service Options */}
                  <div className="space-y-2 sm:space-y-3">
                    {[
                      { text: "Lost my car keys", icon: "fa-search" },
                      { text: "Need a spare key", icon: "fa-clone" },
                      { text: "Key won't work", icon: "fa-exclamation-triangle" },
                      { text: "Locked out of car", icon: "fa-lock" },
                      { text: "Smart key issues", icon: "fa-microchip" },
                      { text: "Key programming", icon: "fa-cog" }
                    ].map((service, index) => (
                                                          <button
                                      key={index}
                                      onClick={() => navigate('/manufacturers')}
                        className="w-full bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-4 py-2 sm:py-3 text-left text-white hover:bg-white/20 hover:border-carkey-red/50 transition-all duration-300 group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <i className={`fas ${service.icon} text-carkey-red text-sm`}></i>
                            <span className="font-medium text-sm">{service.text}</span>
                          </div>
                          <i className="fas fa-chevron-right text-carkey-red group-hover:translate-x-1 transition-transform duration-300 text-xs"></i>
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  {/* Call to Action */}
                  <div className="mt-4 sm:mt-6 text-center">
                    <button
                      onClick={() => navigate('/manufacturers')}
                      className="w-full bg-gradient-to-r from-carkey-red to-red-600 text-white px-6 py-3 rounded-xl font-semibold text-base hover:shadow-2xl hover:shadow-carkey-red/25 hover:-translate-y-1 transition-all duration-500"
                    >
                      <i className="fas fa-paper-plane mr-2"></i>
                      Get Instant Quote
                    </button>
                  </div>
                </div>
                
                {/* Compact Stats */}
                <div className="grid grid-cols-3 gap-3 mt-6">
                  {[
                    { value: '10+', label: 'Years', icon: 'fa-award' },
                                         { value: 'Fast', label: 'Service', icon: 'fa-clock' },
                    { value: '100%', label: 'Mobile', icon: 'fa-truck' }
                  ].map((stat, i) => (
                    <div key={i} className="text-center bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 hover:bg-white/20 transition-all duration-300">
                      <i className={`fas ${stat.icon} text-carkey-red mb-1 text-sm`}></i>
                      <div className="text-white font-bold text-lg">{stat.value}</div>
                      <div className="text-gray-400 text-xs">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        


              </section>

      {/* Car Brand Logos Section */}
      <section className="bg-white py-16 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full bg-repeat" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='20' cy='20' r='2'/%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        <div className="container-max relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-gradient-to-r from-carkey-red to-red-600 text-white rounded-full px-6 py-3 mb-6 shadow-lg">
              <i className="fas fa-car mr-2"></i>
              <span className="text-sm font-bold">ALL BRANDS SUPPORTED</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              We program keys for <span className="text-carkey-red">every major brand</span>
            </h2>
            <p className="text-gray-600 text-lg">Real logos, real expertise - from European luxury to Japanese reliability</p>
          </div>
          
          {/* Mobile-Responsive Brand Logos Grid */}
          <div className="relative">
            {/* Desktop Carousel (hidden on mobile) */}
            <div className="hidden lg:block">
              <div className="overflow-hidden">
                <div className="flex animate-scroll hover:animate-none">
                  {/* First set of logos */}
                  <div className="flex space-x-12 min-w-full justify-around items-center">
                    {[
                      { name: 'Audi', logo: 'audi.png' },
                      { name: 'BMW', logo: 'bmw.png' },
                      { name: 'Ford', logo: 'ford.png' },
                      { name: 'Honda', logo: 'honda.png' },
                      { name: 'Hyundai', logo: 'hyundai.png' },
                      { name: 'Jaguar', logo: 'jaguar.png' },
                      { name: 'Kia', logo: 'kia.png' },
                      { name: 'Lexus', logo: 'lexus.png' },
                      { name: 'Mazda', logo: 'mazda.png' },
                      { name: 'Mini', logo: 'mini.png' }
                    ].map((brand) => (
                      <div key={brand.name} className="group flex flex-col items-center space-y-3 min-w-0 transform hover:scale-110 transition-all duration-300">
                        <div className="w-20 h-20 bg-white rounded-2xl shadow-lg border border-gray-200 flex items-center justify-center group-hover:shadow-2xl group-hover:border-carkey-red transition-all duration-300 p-3">
                          <img 
                            src={`/tmp/${brand.logo}`} 
                            alt={brand.name} 
                            className="max-w-full max-h-full object-contain filter group-hover:brightness-110"
                          />
                        </div>
                        <div className="text-black font-semibold text-sm group-hover:text-carkey-red transition-colors whitespace-nowrap">{brand.name}</div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Second set of logos */}
                  <div className="flex space-x-12 min-w-full justify-around items-center">
                    {[
                      { name: 'Nissan', logo: 'nissan.png' },
                      { name: 'Subaru', logo: 'subaru.png' },
                      { name: 'Suzuki', logo: 'suzuki.png' },
                      { name: 'Toyota', logo: 'toyota.png' },
                      { name: 'Volkswagen', logo: 'volkswagen.png' },
                      { name: 'Volvo', logo: 'volvo.png' },
                      { name: 'Holden', logo: 'holden.png' },
                      { name: 'Jeep', logo: 'jeep.png' },
                      { name: 'Infiniti', logo: 'infiniti.png' },
                      { name: 'Genesis', logo: 'genesis.png' }
                    ].map((brand) => (
                      <div key={brand.name} className="group flex flex-col items-center space-y-3 min-w-0 transform hover:scale-110 transition-all duration-300">
                        <div className="w-20 h-20 bg-white rounded-2xl shadow-lg border border-gray-200 flex items-center justify-center group-hover:shadow-2xl group-hover:border-carkey-red transition-all duration-300 p-3">
                          <img 
                            src={`/tmp/${brand.logo}`} 
                            alt={brand.name} 
                            className="max-w-full max-h-full object-contain filter group-hover:brightness-110"
                          />
                        </div>
                        <div className="text-black font-semibold text-sm group-hover:text-carkey-red transition-colors whitespace-nowrap">{brand.name}</div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Third set of logos */}
                  <div className="flex space-x-12 min-w-full justify-around items-center">
                    {[
                      { name: 'Ferrari', logo: 'ferrari.png' },
                      { name: 'Fiat', logo: 'fiat.png' },
                      { name: 'Maserati', logo: 'maserati.png' },
                      { name: 'Lotus', logo: 'lotus.png' },
                      { name: 'Abarth', logo: 'abarth.png' },
                      { name: 'Škoda', logo: 'skoda.png' },
                      { name: 'Chery', logo: 'chery.png' },
                      { name: 'Chevrolet', logo: 'chevrolet.png' },
                      { name: 'Datsun', logo: 'datsun.png' },
                      { name: 'Eicher', logo: 'eicher.png' }
                    ].map((brand) => (
                      <div key={brand.name} className="group flex flex-col items-center space-y-3 min-w-0 transform hover:scale-110 transition-all duration-300">
                        <div className="w-20 h-20 bg-white rounded-2xl shadow-lg border border-gray-200 flex items-center justify-center group-hover:shadow-2xl group-hover:border-carkey-red transition-all duration-300 p-3">
                          <img 
                            src={`/tmp/${brand.logo}`} 
                            alt={brand.name} 
                            className="max-w-full max-h-full object-contain filter group-hover:brightness-110"
                          />
                        </div>
                        <div className="text-black font-semibold text-sm group-hover:text-carkey-red transition-colors whitespace-nowrap">{brand.name}</div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Fourth set of logos */}
                  <div className="flex space-x-12 min-w-full justify-around items-center">
                    {[
                      { name: 'GMC', logo: 'gmc.png' },
                      { name: 'Isuzu', logo: 'isuzu.png' },
                      { name: 'JMC', logo: 'jmc.png' },
                      { name: 'Mahindra', logo: 'mahindra.png' },
                      { name: 'Renault Samsung', logo: 'renault-samsung.png' },
                      { name: 'Tata', logo: 'tata.png' }
                    ].map((brand) => (
                      <div key={brand.name} className="group flex flex-col items-center space-y-3 min-w-0 transform hover:scale-110 transition-all duration-300">
                        <div className="w-20 h-20 bg-white rounded-2xl shadow-lg border border-gray-200 flex items-center justify-center group-hover:shadow-2xl group-hover:border-carkey-red transition-all duration-300 p-3">
                          <img 
                            src={`/tmp/${brand.logo}`} 
                            alt={brand.name} 
                            className="max-w-full max-h-full object-contain filter group-hover:brightness-110"
                          />
                        </div>
                        <div className="text-black font-semibold text-sm group-hover:text-carkey-red transition-colors whitespace-nowrap">{brand.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Gradient Masks for Desktop */}
              <div className="absolute left-0 top-0 w-32 h-full bg-gradient-to-r from-white to-transparent pointer-events-none"></div>
              <div className="absolute right-0 top-0 w-32 h-full bg-gradient-to-l from-white to-transparent pointer-events-none"></div>
            </div>

            {/* Mobile & Tablet Responsive Grid */}
            <div className="lg:hidden">
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4 sm:gap-6">
                {[
                  { name: 'Audi', logo: 'audi.png' },
                  { name: 'BMW', logo: 'bmw.png' },
                  { name: 'Ford', logo: 'ford.png' },
                  { name: 'Honda', logo: 'honda.png' },
                  { name: 'Hyundai', logo: 'hyundai.png' },
                  { name: 'Jaguar', logo: 'jaguar.png' },
                  { name: 'Kia', logo: 'kia.png' },
                  { name: 'Lexus', logo: 'lexus.png' },
                  { name: 'Mazda', logo: 'mazda.png' },
                  { name: 'Mini', logo: 'mini.png' },
                  { name: 'Nissan', logo: 'nissan.png' },
                  { name: 'Subaru', logo: 'subaru.png' },
                  { name: 'Suzuki', logo: 'suzuki.png' },
                  { name: 'Toyota', logo: 'toyota.png' },
                  { name: 'Volkswagen', logo: 'volkswagen.png' },
                  { name: 'Volvo', logo: 'volvo.png' },
                  { name: 'Holden', logo: 'holden.png' },
                  { name: 'Jeep', logo: 'jeep.png' },
                  { name: 'Infiniti', logo: 'infiniti.png' },
                  { name: 'Genesis', logo: 'genesis.png' },
                  { name: 'Ferrari', logo: 'ferrari.png' },
                  { name: 'Fiat', logo: 'fiat.png' },
                  { name: 'Maserati', logo: 'maserati.png' },
                  { name: 'Lotus', logo: 'lotus.png' },
                  { name: 'Abarth', logo: 'abarth.png' },
                  { name: 'Škoda', logo: 'skoda.png' },
                  { name: 'Chery', logo: 'chery.png' },
                  { name: 'Chevrolet', logo: 'chevrolet.png' },
                  { name: 'Datsun', logo: 'datsun.png' },
                  { name: 'Eicher', logo: 'eicher.png' },
                  { name: 'GMC', logo: 'gmc.png' },
                  { name: 'Isuzu', logo: 'isuzu.png' },
                  { name: 'JMC', logo: 'jmc.png' },
                  { name: 'Mahindra', logo: 'mahindra.png' },
                  { name: 'Renault Samsung', logo: 'renault-samsung.png' },
                  { name: 'Tata', logo: 'tata.png' }
                ].map((brand) => (
                  <div key={brand.name} className="group flex flex-col items-center space-y-2 transform hover:scale-105 transition-all duration-300">
                    <div className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 flex items-center justify-center group-hover:shadow-xl group-hover:border-carkey-red transition-all duration-300 p-2 sm:p-3">
                      <img 
                        src={`/tmp/${brand.logo}`} 
                        alt={brand.name} 
                        className="max-w-full max-h-full object-contain filter group-hover:brightness-110"
                      />
                    </div>
                    <div className="text-black font-semibold text-xs sm:text-sm group-hover:text-carkey-red transition-colors text-center leading-tight">{brand.name}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Bottom Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            <div className="text-center bg-white shadow-xl border border-gray-200 rounded-xl p-6 transform hover:scale-105 transition-all duration-300">
              <div className="text-carkey-red font-black text-3xl mb-1">36+</div>
              <div className="text-black font-semibold">Car Brands</div>
            </div>
            <div className="text-center bg-white shadow-xl border border-gray-200 rounded-xl p-6 transform hover:scale-105 transition-all duration-300">
              <div className="text-carkey-red font-black text-3xl mb-1">500+</div>
              <div className="text-black font-semibold">Car Models</div>
            </div>
            <div className="text-center bg-white shadow-xl border border-gray-200 rounded-xl p-6 transform hover:scale-105 transition-all duration-300">
              <div className="text-carkey-red font-black text-3xl mb-1">99%</div>
              <div className="text-black font-semibold">Success Rate</div>
            </div>
                         <div className="text-center bg-white shadow-xl border border-gray-200 rounded-xl p-6 transform hover:scale-105 transition-all duration-300">
               <div className="text-carkey-red font-black text-3xl mb-1">Fast</div>
               <div className="text-black font-semibold">Response</div>
             </div>
          </div>
        </div>
      </section>

      {/* Premium Services Section */}
      <section id="services" className="relative bg-gradient-to-br from-slate-50 via-white to-gray-50 overflow-hidden">
        {/* Premium Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-carkey-red/5 to-transparent rounded-full blur-3xl animate-float-slow"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-gradient-to-l from-blue-500/5 to-transparent rounded-full blur-3xl animate-float-slow-reverse"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23000000' stroke-width='1'%3E%3Cpath d='M0 30h60M30 0v60'/%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="container-max section-padding relative z-10">
          {/* Premium Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-gradient-to-r from-carkey-red/10 to-red-500/10 backdrop-blur-sm border border-carkey-red/20 text-carkey-red rounded-full px-6 py-3 mb-8 shadow-lg">
              <div className="w-2 h-2 bg-carkey-red rounded-full mr-3 animate-pulse"></div>
              <span className="text-sm font-semibold tracking-wide">PREMIUM SERVICES</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              <span className="block opacity-0 animate-fade-in-up">What We</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-carkey-red via-red-500 to-orange-500 opacity-0 animate-fade-in-up-delay-1">
                Master
              </span>
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed opacity-0 animate-fade-in-up-delay-2">
              Precision-engineered automotive key solutions delivered with uncompromising quality and professional expertise
            </p>
          </div>
          
          {/* Premium Service Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: 'fa-key',
                title: 'Lost Key Recovery',
                description: 'Complete key replacement with advanced security programming',
                features: ['Emergency Response', 'Security Verification', 'Instant Programming'],
                gradient: 'from-red-500 to-red-600',
                delay: '0.3s'
              },
              {
                icon: 'fa-clone',
                title: 'Spare Key Creation',
                description: 'Additional keys for family members or backup security',
                features: ['Family Access', 'Backup Security', 'Quick Duplication'],
                gradient: 'from-blue-500 to-blue-600',
                delay: '0.4s'
              },
              {
                icon: 'fa-microchip',
                title: 'Smart Key Technology',
                description: 'Modern smart keys and remote fobs with advanced programming',
                features: ['RFID Technology', 'Remote Programming', 'Security Encryption'],
                gradient: 'from-purple-500 to-purple-600',
                delay: '0.5s'
              },
              {
                icon: 'fa-unlock',
                title: 'Keyless Entry Systems',
                description: 'Advanced keyless entry programming and system repair',
                features: ['Proximity Sensors', 'Touch Entry', 'System Diagnostics'],
                gradient: 'from-green-500 to-green-600',
                delay: '0.6s'
              },
              {
                icon: 'fa-wifi',
                title: 'Transponder Security',
                description: 'Chip key cutting and programming for maximum security',
                features: ['Chip Programming', 'Security Codes', 'Anti-Theft Protection'],
                gradient: 'from-orange-500 to-orange-600',
                delay: '0.7s'
              },
              {
                icon: 'fa-shield-alt',
                title: 'Premium Warranty',
                description: 'Comprehensive 12-month warranty on all services',
                features: ['Full Coverage', 'Parts Guarantee', 'Service Warranty'],
                gradient: 'from-teal-500 to-teal-600',
                delay: '0.8s'
              }
            ].map((service, index) => (
              <div 
                key={index}
                className="group relative bg-white/80 backdrop-blur-sm border border-white/50 rounded-3xl p-8 hover:bg-white hover:shadow-2xl hover:shadow-gray-900/10 transition-all duration-700 hover:-translate-y-2 cursor-pointer overflow-hidden opacity-0 animate-fade-in-up"
                style={{animationDelay: service.delay}}
              >
                {/* Hover Background Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                
                {/* Service Icon */}
                <div className="relative z-10 mb-6">
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${service.gradient} rounded-2xl shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-500`}>
                    <i className={`fas ${service.icon} text-white text-2xl`}></i>
                  </div>
                </div>
                
                {/* Service Content */}
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-carkey-red transition-colors duration-300">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  {/* Feature List */}
                  <div className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-carkey-red rounded-full flex-shrink-0"></div>
                        <span className="text-sm text-gray-700 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Hover Arrow */}
                <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-500">
                  <div className="w-8 h-8 bg-gradient-to-r from-carkey-red to-red-500 rounded-full flex items-center justify-center">
                    <i className="fas fa-arrow-right text-white text-sm"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="bg-white">
        <div className="container-max section-padding">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-carkey-dark mb-4">Three simple steps to get your car key replaced in Perth</h2>
            <p className="text-xl text-carkey-gray">Getting back on the road has never been easier</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-carkey-red text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">1</div>
              <h3 className="text-xl font-semibold text-carkey-dark mb-4">Get a quick quote</h3>
              <p className="text-carkey-gray">Fill out the form or message via WhatsApp for an immediate price.</p>
            </div>
            <div className="text-center">
              <div className="bg-carkey-red text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">2</div>
              <h3 className="text-xl font-semibold text-carkey-dark mb-4">Confirm your booking</h3>
              <p className="text-carkey-gray">Schedule a convenient time; our technician comes to you.</p>
            </div>
            <div className="text-center">
              <div className="bg-carkey-red text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">3</div>
              <h3 className="text-xl font-semibold text-carkey-dark mb-4">Get back on the road</h3>
              <p className="text-carkey-gray">We cut/program your new key while you wait, so you're driving again with a smile.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="bg-gray-50">
        <div className="container-max section-padding">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-carkey-dark mb-4">Why choose Carkey Solutions Perth for car key replacement?</h2>
            <p className="text-xl text-carkey-gray">Perth's trusted car key specialists</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <i className="fas fa-truck text-4xl text-carkey-red mb-4"></i>
              <h3 className="text-xl font-semibold text-carkey-dark mb-2">Mobile service</h3>
              <p className="text-carkey-gray">We come to you anywhere in Perth.</p>
            </div>
            <div className="text-center p-6">
              <i className="fas fa-clock text-4xl text-carkey-red mb-4"></i>
              <h3 className="text-xl font-semibold text-carkey-dark mb-2">Fast response guarantee</h3>
              <p className="text-carkey-gray">We respond within hours, no long waits.</p>
            </div>
            <div className="text-center p-6">
              <i className="fas fa-car text-4xl text-carkey-red mb-4"></i>
              <h3 className="text-xl font-semibold text-carkey-dark mb-2">All makes & models</h3>
              <p className="text-carkey-gray">European, Japanese or otherwise, we've got the right key.</p>
            </div>
            <div className="text-center p-6">
              <i className="fas fa-dollar-sign text-4xl text-carkey-red mb-4"></i>
              <h3 className="text-xl font-semibold text-carkey-dark mb-2">Fair pricing</h3>
              <p className="text-carkey-gray">Transparent quotes with no hidden fees.</p>
            </div>
            <div className="text-center p-6">
              <i className="fas fa-user-friends text-4xl text-carkey-red mb-4"></i>
              <h3 className="text-xl font-semibold text-carkey-dark mb-2">Friendly experts</h3>
              <p className="text-carkey-gray">Experienced technicians who care about your experience.</p>
            </div>
            <div className="text-center p-6">
              <i className="fas fa-heart text-4xl text-carkey-red mb-4"></i>
              <h3 className="text-xl font-semibold text-carkey-dark mb-2">Peace of mind</h3>
              <p className="text-carkey-gray">12-month warranty on every key.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-carkey-red text-white">
        <div className="container-max section-padding">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Get back on the road today with Perth's best car key service</h2>
            <p className="text-xl">Stuck without your keys? Call or message us now and we'll get you moving again.</p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <div className="mb-8">
                <h3 className="text-2xl font-semibold mb-6">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <i className="fas fa-map-marker-alt text-xl"></i>
                    <span>70 Division Street, Welshpool 6106</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="fas fa-phone text-xl"></i>
                    <a href="tel:+61430168669" className="hover:underline">+61 430 168 669</a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="fab fa-whatsapp text-xl"></i>
                    <a href="https://wa.me/61430168669" className="hover:underline">WhatsApp Us</a>
                  </div>
                </div>
              </div>
              
              <div className="bg-red-800 p-6 rounded-lg">
                <p className="text-lg mb-2">
                  <i className="fas fa-clock mr-2"></i>
                  Quick Response Guarantee
                </p>
                <p>We respect your time and respond quickly to all enquiries. Most quotes provided within the hour.</p>
              </div>
            </div>
            
            {/* Contact Form */}
            <div>
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={contactForm.name}
                      onChange={handleContactFormChange}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-white text-carkey-dark focus:ring-2 focus:ring-red-300 focus:outline-none"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium mb-2">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={contactForm.phone}
                      onChange={handleContactFormChange}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-white text-carkey-dark focus:ring-2 focus:ring-red-300 focus:outline-none"
                      placeholder="Your phone number"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactFormChange}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white text-carkey-dark focus:ring-2 focus:ring-red-300 focus:outline-none"
                    placeholder="Your email"
                  />
                </div>
                
                <div>
                  <label htmlFor="vehicle" className="block text-sm font-medium mb-2">Vehicle Make/Model</label>
                  <input
                    type="text"
                    id="vehicle"
                    name="vehicle"
                    value={contactForm.vehicle}
                    onChange={handleContactFormChange}
                    className="w-full px-4 py-3 rounded-lg bg-white text-carkey-dark focus:ring-2 focus:ring-red-300 focus:outline-none"
                    placeholder="e.g. Toyota Camry 2020"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactFormChange}
                    rows="4"
                    className="w-full px-4 py-3 rounded-lg bg-white text-carkey-dark focus:ring-2 focus:ring-red-300 focus:outline-none"
                    placeholder="Tell us about your key replacement needs..."
                  ></textarea>
                </div>

                {/* Human Verification */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Human Verification <span className="text-red-600">*</span>
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2 bg-gray-100 rounded-lg px-4 py-3">
                      <span className="text-carkey-dark text-lg font-bold">{captchaNumbers.num1}</span>
                      <span className="text-carkey-dark text-lg">+</span>
                      <span className="text-carkey-dark text-lg font-bold">{captchaNumbers.num2}</span>
                      <span className="text-carkey-dark text-lg">=</span>
                    </div>
                    <input
                      type="number"
                      value={captchaAnswer}
                      onChange={handleCaptchaChange}
                      required
                      className="w-24 px-4 py-3 bg-white border border-gray-300 rounded-lg text-carkey-dark text-center font-bold text-lg focus:ring-2 focus:ring-red-300 focus:outline-none"
                      placeholder="?"
                    />
                    <button
                      type="button"
                      onClick={generateCaptcha}
                      className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-carkey-dark hover:bg-gray-200 transition-colors"
                      title="Generate new calculation"
                    >
                      <i className="fas fa-refresh"></i>
                    </button>
                  </div>
                  {captchaAnswer && !captchaCorrect && (
                    <p className="text-red-600 text-sm mt-2">Incorrect answer. Please try again.</p>
                  )}
                  {captchaCorrect && (
                    <p className="text-green-600 text-sm mt-2">✓ Verification successful!</p>
                  )}
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-100 border border-green-400 rounded-lg p-4 text-center">
                    <i className="fas fa-check-circle text-green-600 text-xl mb-2"></i>
                    <p className="text-green-800 font-semibold">Thank you! Your message has been sent successfully.</p>
                    <p className="text-green-700 text-sm mt-1">We'll get back to you within 1 hour.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-100 border border-red-400 rounded-lg p-4 text-center">
                    <i className="fas fa-exclamation-circle text-red-600 text-xl mb-2"></i>
                    <p className="text-red-800 font-semibold">Sorry, there was an error sending your message.</p>
                    <p className="text-red-700 text-sm mt-1">Please try again or call us directly on +61 430 168 669.</p>
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={isSubmitting || !captchaCorrect}
                  className={`w-full px-8 py-4 rounded-lg font-semibold transition-colors duration-300 ${
                    isSubmitting || !captchaCorrect
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-white text-carkey-red hover:bg-gray-100'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-carkey-dark text-white">
        {/* Emergency Contact Top Section */}
        <div className="relative bg-gradient-to-r from-slate-900 via-gray-900 to-slate-900 border-b border-gray-700/50 overflow-hidden">
          {/* Wave Graphics */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='20' viewBox='0 0 100 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 10 Q 25 0, 50 10 T 100 10 L 100 20 L 0 20 Z' fill='%23dc2626'/%3E%3C/svg%3E")`,
              backgroundSize: '100px 20px',
              backgroundRepeat: 'repeat-x'
            }}></div>
          </div>
          
          {/* Animated Wave Overlay */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 left-0 w-full h-full animate-pulse" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='120' height='25' viewBox='0 0 120 25' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 15 Q 30 5, 60 15 T 120 15 L 120 25 L 0 25 Z' fill='%23ffffff'/%3E%3C/svg%3E")`,
              backgroundSize: '120px 25px',
              backgroundRepeat: 'repeat-x'
            }}></div>
          </div>
          
          <div className="container-max py-3 sm:py-6 px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              {/* Emergency Badge */}
              <div className="inline-flex items-center bg-gradient-to-r from-carkey-red/20 to-red-600/20 backdrop-blur-sm border border-carkey-red/30 text-carkey-red rounded-full px-3 sm:px-4 py-1 sm:py-2 mb-2 sm:mb-4 shadow-lg">
                <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-carkey-red rounded-full mr-1.5 sm:mr-2 animate-pulse"></div>
                <span className="text-xs sm:text-sm font-semibold tracking-wide">EMERGENCY SERVICE</span>
              </div>
              
              {/* Phone Number */}
              <a 
                href="tel:+61430168669" 
                className="block text-2xl sm:text-3xl md:text-4xl font-bold text-carkey-red hover:text-red-400 transition-all duration-300 hover:scale-105"
              >
                +61 430 168 669
              </a>
              
              {/* Service Description */}
                               <p className="text-base sm:text-lg text-gray-300 mt-1 sm:mt-2 font-medium">Professional Service</p>
                              <p className="text-xs sm:text-sm text-gray-400 mt-0.5 sm:mt-1">Professional car key assistance when you need it</p>
              
              {/* Service Areas - Compact */}
              <div className="mt-2 sm:mt-4 text-xs text-gray-400">
                <div className="flex flex-wrap justify-center gap-x-2 sm:gap-x-3 gap-y-0.5 sm:gap-y-1">
                  {['Perth', 'Fremantle', 'Joondalup', 'Rockingham', 'Canning Vale', 'Scarborough', 'Mount Lawley', 'Subiaco', 'Leederville', 'Northbridge', 'South Perth', 'Victoria Park', 'Como', 'Applecross', 'Melville', 'Cockburn', 'Kwinana', 'Baldivis', 'Secret Harbour', 'Singleton', 'Warnbro', 'Safety Bay', 'Wellard', 'Bertram', 'Atwell', 'Success', 'Piara Waters', 'Harrisdale', 'Armadale', 'Gosnells', 'Thornlie', 'Maddington', 'Kenwick', 'Lynwood', 'Riverton', 'Shelley', 'Willetton', 'Bull Creek', 'Leeming', 'Bateman', 'Murdoch', 'Kardinya', 'Winthrop'].map((area, index) => (
                    <span key={index} className="hover:text-gray-300 transition-colors duration-200">
                      {area}
                      {index < 49 && ' • '}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="container-max">
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
            <div>
              <div className="text-2xl font-bold mb-4">
                <span className="text-carkey-red">Car</span>key Solutions Perth
              </div>
              <p className="text-gray-400 mb-4">
                Perth's trusted mobile car key replacement service. Professional, fast, and reliable.
              </p>
              <SocialShare />
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => navigate('/')} className="hover:text-white transition-colors">Home</button></li>
                <li><button onClick={() => navigate('/manufacturers')} className="hover:text-white transition-colors">Services</button></li>
                <li><button onClick={() => navigate('/quote')} className="hover:text-white transition-colors">Get Quote</button></li>
                <li><a href="tel:+61430168669" className="hover:text-white transition-colors">Call Us</a></li>
                <li><a href="https://wa.me/61430168669" className="hover:text-white transition-colors">Message Us</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Our Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Lost Keys</li>
                <li>Spare Keys</li>
                <li>Smart Keys & Fobs</li>
                <li>Keyless Entry</li>
                <li>Transponder Keys</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 py-6 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} Carkey Solutions. All rights reserved.
              </p>
              <div className="flex items-center space-x-4 mt-4 md:mt-0">
                <a href="https://www.facebook.com/Carkeysolutions.net.au/?_rdr" className="text-gray-400 hover:text-white transition-colors" aria-label="Facebook">
                  <i className="fab fa-facebook"></i>
                </a>
                <span className="text-gray-500">|</span>
                <a 
                  href="https://www.alltechzone.au" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-gray-500 hover:text-carkey-red transition-colors duration-300 group"
                  title="Website designed and developed by AllTechZone - Professional Web Development Services in Perth"
                >
                  <span className="mr-1">Designed by</span>
                  <span className="font-semibold group-hover:text-carkey-red transition-colors duration-300">AllTechZone</span>
                  <i className="fas fa-external-link-alt ml-1 text-xs opacity-60 group-hover:opacity-100 transition-opacity duration-300"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Chat Button */}
      <div className="fixed bottom-6 right-6 z-50">
        {/* Chat Options */}
        {isChatOpen && (
          <div className="mb-4 space-y-3 animate-fade-in-up">
            {/* Call Button */}
            <a 
              href="tel:+61430168669"
              className="group flex items-center justify-center w-14 h-14 bg-gradient-to-r from-green-500 to-green-600 rounded-full shadow-2xl hover:shadow-green-500/25 transition-all duration-300 hover:scale-110"
            >
              <i className="fas fa-phone text-white text-xl group-hover:animate-pulse"></i>
            </a>
            
            {/* WhatsApp Button */}
            <a 
              href="https://wa.me/61430168669"
              className="group flex items-center justify-center w-14 h-14 bg-gradient-to-r from-green-400 to-green-500 rounded-full shadow-2xl hover:shadow-green-400/25 transition-all duration-300 hover:scale-110"
            >
              <i className="fab fa-whatsapp text-white text-xl group-hover:animate-pulse"></i>
            </a>
          </div>
        )}
        
        {/* Main Chat Toggle Button */}
        <button
          onClick={() => setIsChatOpen(!isChatOpen)}
          className={`group flex items-center justify-center w-16 h-16 rounded-full shadow-2xl transition-all duration-500 hover:scale-110 ${
            isChatOpen 
              ? 'bg-gradient-to-r from-carkey-red to-red-600 shadow-red-500/25' 
              : 'bg-gradient-to-r from-carkey-red to-red-600 shadow-carkey-red/25'
          }`}
        >
          <i className={`fas text-white text-2xl transition-all duration-300 ${
            isChatOpen ? 'fa-times' : 'fa-comments'
          }`}></i>
        </button>
        
        {/* Tooltip */}
        {!isChatOpen && (
          <div className="absolute right-20 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            Need help? Chat with us!
            <div className="absolute left-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-l-4 border-l-gray-900 border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HomePage;
